import { supabase } from "@/lib/supabase";

// ─── Types ──────────────────────────────────────────────────────────────────

export type ContactType =
    | "family"
    | "legal_guardian"
    | "emergency"
    | "doctor"
    | "pharmacy"
    | "therapist"
    | "other";

export interface ResidentContact {
    id: string;
    residentId: string;
    residentName: string;
    contactType: ContactType;
    name: string;
    relationship: string | null;
    phone: string | null;
    mobile: string | null;
    email: string | null;
    address: string | null;
    isPrimary: boolean;
    isEmergency: boolean;
    callOrder: number | null;
    notes: string | null;
    createdAt: string;
}

export const CONTACT_TYPE_CONFIG: Record<
    ContactType,
    { label: string; color: string; bg: string }
> = {
    family: {
        label: "Angehörige",
        color: "text-brand-700 dark:text-brand-400",
        bg: "bg-brand-100 dark:bg-brand-900/30",
    },
    legal_guardian: {
        label: "Betreuer",
        color: "text-purple-700 dark:text-purple-400",
        bg: "bg-purple-100 dark:bg-purple-900/30",
    },
    emergency: {
        label: "Notfallkontakt",
        color: "text-red-700 dark:text-red-400",
        bg: "bg-red-100 dark:bg-red-900/30",
    },
    doctor: {
        label: "Arzt",
        color: "text-blue-700 dark:text-blue-400",
        bg: "bg-blue-100 dark:bg-blue-900/30",
    },
    pharmacy: {
        label: "Apotheke",
        color: "text-emerald-700 dark:text-emerald-400",
        bg: "bg-emerald-100 dark:bg-emerald-900/30",
    },
    therapist: {
        label: "Therapeut",
        color: "text-amber-700 dark:text-amber-400",
        bg: "bg-amber-100 dark:bg-amber-900/30",
    },
    other: {
        label: "Sonstige",
        color: "text-neutral-600 dark:text-neutral-400",
        bg: "bg-neutral-100 dark:bg-neutral-800",
    },
};

// ─── Queries ────────────────────────────────────────────────────────────────

function mapRow(d: Record<string, unknown>): ResidentContact {
    return {
        id: d.id as string,
        residentId: d.resident_id as string,
        residentName: d.resident_name as string,
        contactType: (d.contact_type as ContactType) ?? "other",
        name: d.name as string,
        relationship: d.relationship as string | null,
        phone: d.phone as string | null,
        mobile: d.mobile as string | null,
        email: d.email as string | null,
        address: d.address as string | null,
        isPrimary: Boolean(d.is_primary),
        isEmergency: Boolean(d.is_emergency),
        callOrder: d.call_order as number | null,
        notes: d.notes as string | null,
        createdAt: d.created_at as string,
    };
}

export async function getContacts(
    facilityId: string,
    filters?: {
        contactType?: ContactType;
        search?: string;
        residentId?: string;
    },
): Promise<ResidentContact[]> {
    // resident_contacts joined via resident → facility
    let q = supabase
        .from("resident_contacts")
        .select("*, residents!inner(facility_id, first_name, last_name)")
        .eq("residents.facility_id", facilityId)
        .order("name", { ascending: true });

    if (filters?.contactType) q = q.eq("contact_type", filters.contactType);
    if (filters?.residentId) q = q.eq("resident_id", filters.residentId);
    if (filters?.search) q = q.ilike("name", `%${filters.search}%`);

    const { data, error } = await q.limit(500);
    if (error) throw new Error(error.message);

    return (data ?? []).map((d) => {
        const resident = (d as Record<string, unknown>).residents as {
            first_name?: string;
            last_name?: string;
        } | null;
        return {
            ...mapRow(d),
            residentName: resident
                ? `${resident.first_name ?? ""} ${resident.last_name ?? ""}`.trim()
                : "Unbekannt",
        };
    });
}

export async function getContactsByResident(residentId: string): Promise<ResidentContact[]> {
    const { data, error } = await supabase
        .from("resident_contacts")
        .select("*")
        .eq("resident_id", residentId)
        .order("call_order", { ascending: true });

    if (error) throw new Error(error.message);
    return (data ?? []).map(mapRow);
}

export async function createContact(input: {
    residentId: string;
    contactType: ContactType;
    name: string;
    relationship?: string;
    phone?: string;
    mobile?: string;
    email?: string;
    address?: string;
    isPrimary?: boolean;
    isEmergency?: boolean;
    callOrder?: number;
    notes?: string;
}): Promise<ResidentContact> {
    const { data, error } = await supabase
        .from("resident_contacts")
        .insert({
            resident_id: input.residentId,
            contact_type: input.contactType,
            name: input.name,
            relationship: input.relationship ?? null,
            phone: input.phone ?? null,
            mobile: input.mobile ?? null,
            email: input.email ?? null,
            address: input.address ?? null,
            is_primary: input.isPrimary ?? false,
            is_emergency: input.isEmergency ?? false,
            call_order: input.callOrder ?? null,
            notes: input.notes ?? null,
        })
        .select()
        .single();

    if (error) throw new Error(error.message);
    return mapRow(data);
}

export async function deleteContact(id: string): Promise<void> {
    const { error } = await supabase.from("resident_contacts").delete().eq("id", id);

    if (error) throw new Error(error.message);
}

export async function getContactStats(facilityId: string): Promise<{
    total: number;
    emergency: number;
    doctors: number;
    families: number;
}> {
    const { data, error } = await supabase
        .from("resident_contacts")
        .select("contact_type, residents!inner(facility_id)")
        .eq("residents.facility_id", facilityId);

    if (error) throw new Error(error.message);
    const rows = data ?? [];
    return {
        total: rows.length,
        emergency: rows.filter((r) => r.contact_type === "emergency").length,
        doctors: rows.filter((r) => r.contact_type === "doctor").length,
        families: rows.filter((r) => r.contact_type === "family").length,
    };
}
